import './Button.css'
import cx from 'classnames'
import { ButtonHTMLAttributes } from 'react'
import { Spinner } from '../Spinner/Spinner'
import { Tooltip } from '../Tooltip/Tooltip'

export type ButtonVariant =
  | 'default'
  | 'subtle'
  | 'warning'
  | 'danger'
  | 'txt-default'
  | 'txt-danger'
  | 'neutral'
export type ButtonSize = 'xs' | 'sm' | 'md'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isLoading?: boolean
  sizeType?: ButtonSize
  variant?: ButtonVariant
  icon?: React.ReactNode
  iconPosition?: 'left'
  tooltip?: string
}

// Export these so other components can reuse button styles
export const buttonVariantClasses: Record<ButtonVariant, string> = {
  default: 'btn-default',
  subtle: 'btn-subtle',
  warning: 'btn-warning',
  danger: 'btn-danger',
  'txt-default': 'btn-txt-default',
  'txt-danger': 'btn-txt-danger',
  neutral: 'btn-neutral'
}

export const buttonSizeClasses: Record<ButtonSize, string> = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: 'btn-md'
}

// For backward compatibility with components that need base button styling
export const buttonClasses = {
  variantToClasses: buttonVariantClasses,
  sizeTypeToClasses: buttonSizeClasses,
  // btn-base contains core button styles (border, rounded, transitions, etc.)
  buttonBaseClassName: 'btn-base',
  btnBaseIcon: 'btn-base-icon'
}

export function Button({
  children,
  className,
  isLoading,
  icon,
  tooltip,
  iconPosition = 'left',
  sizeType = 'sm',
  variant = 'default',
  ...buttonProps
}: ButtonProps) {
  return tooltip ? (
    <Tooltip content={tooltip}>
      <button
        className={cx(
          buttonClasses.buttonBaseClassName,
          buttonSizeClasses[sizeType],
          buttonVariantClasses[variant],
          !!icon && buttonClasses.btnBaseIcon,
          className
        )}
        {...buttonProps}
      >
        {icon && iconPosition === 'left' && icon}
        {isLoading ? <Spinner className="w-4 inline-block" /> : children}
      </button>
    </Tooltip>
  ) : (
    <button
      className={cx(
        buttonClasses.buttonBaseClassName,
        buttonVariantClasses[variant],
        buttonSizeClasses[sizeType],
        !!icon && buttonClasses.btnBaseIcon,
        className
      )}
      {...buttonProps}
    >
      {icon && iconPosition === 'left' && <span className="size-6">{icon}</span>}
      {isLoading ? <Spinner className="w-4 inline-block" /> : children}
    </button>
  )
}
